import { useMutation } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { getApiErrorMessage } from '@noor/api';
import { confirmDialog, modalStore, toast } from '@noor/lib';
import { providerImportApi } from '@noor/eats-api';

/** Trigger the merchant-wide menu batch; its modal owns polling and refresh. */
export function useImportMenu() {
  return useMutation({
    meta: { suppressErrorToast: true },
    mutationFn: () => providerImportApi.importMenu(),
  });
}

/** Trigger the restaurant import; its modal owns polling and refresh. */
export function useImportRestaurants() {
  return useMutation({
    meta: { suppressErrorToast: true },
    mutationFn: () => providerImportApi.importRestaurants(),
  });
}

export function useMenuImportAction() {
  const { t } = useTranslation('eats');
  const importMenu = useImportMenu();

  async function runImport(): Promise<void> {
    await confirmDialog({
      title: t('menu.import.confirm.title'),
      description: t('menu.import.confirm.description'),
      confirmLabel: t('menu.import.confirm.confirm'),
      cancelLabel: t('menu.import.confirm.cancel'),
      onConfirm: () =>
        importMenu.mutateAsync(undefined, {
          onSuccess: ({ batch_job_id: jobId }) =>
            modalStore.open('provider-import-job', { jobId }),
          onError: (error) =>
            toast.error(getApiErrorMessage(error, t('menu.import.error'))),
        }),
    });
  }

  return { runImport, isPending: importMenu.isPending };
}

export function useRestaurantImportAction() {
  const { t } = useTranslation('eats');
  const importRestaurants = useImportRestaurants();

  async function runImport(): Promise<void> {
    await confirmDialog({
      title: t('branches.import.confirm.title'),
      description: t('branches.import.confirm.description'),
      confirmLabel: t('branches.import.confirm.confirm'),
      cancelLabel: t('branches.import.confirm.cancel'),
      onConfirm: () =>
        importRestaurants.mutateAsync(undefined, {
          onSuccess: ({ job_id: jobId }) =>
            modalStore.open('provider-import-job', { jobId }),
          onError: (error) =>
            toast.error(getApiErrorMessage(error, t('branches.import.error'))),
        }),
    });
  }

  return { runImport, isPending: importRestaurants.isPending };
}
